import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useSelection } from '@/contexts/SelectionContext';
import { supabase } from '@/integrations/supabase/client';
import { getGoogleClientId } from '@/lib/googleClientId';
import { toast } from 'sonner';
import { resolveOAuthErrorCopy } from '@/lib/oauthError';
import SyncProgressDialog from './SyncProgressDialog';

interface GoogleOAuthButtonProps {
  onSuccess?: () => void;
  children?: React.ReactNode;
}

const GoogleOAuthButton: React.FC<GoogleOAuthButtonProps> = ({ onSuccess, children }) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { refreshSelection } = useSelection();
  const [showSyncProgress, setShowSyncProgress] = useState(false);
  const [googleAccountId, setGoogleAccountId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  const triggerDataFetch = async (accountId: string) => {
    try {
      await supabase.functions.invoke('fetch-account-data', {
        body: { googleAccountId: accountId, userId: user?.id }
      });
    } catch (error) {
      console.error('Error triggering data fetch:', error);
      Sentry.captureException(error, { tags: { feature: 'google-accounts', action: 'fetch-account-data' } });
    }
  };

  const handleGoogleConnect = async () => {
    if (isConnecting) return;
    if (!user) {
      toast.error(t('oauth.authRequired'), {
        description: t('oauth.authRequiredDesc'),
      });
      return;
    }

    setIsConnecting(true);

    // Open popup immediately (synchronously) to prevent browser blocking
    const popup = window.open(
      'about:blank',
      'google-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    try {
      // Client-ID via gemeinsamen Helper (Retry + localStorage-Fallback, Sentry DENO-T)
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const clientId = await getGoogleClientId();
      const scope = [
        'https://www.googleapis.com/auth/content',
        'openid',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ].join(' ');

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', user.id);
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('include_granted_scopes', 'true');

      // Navigate the pre-opened popup or fallback to full-tab redirect
      if (popup && !popup.closed) {
        popup.location.href = authUrl.toString();
      } else {
        window.location.href = authUrl.toString();
        return;
      }

      // Listen for OAuth callback
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          popup?.close();
          setIsConnecting(false);

          const accountId = event.data.data.googleAccountId;
          setGoogleAccountId(accountId);

          // Immediately trigger data fetch and show sync progress
          triggerDataFetch(accountId);
          setShowSyncProgress(true);

          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          popup?.close();
          setIsConnecting(false);
          // Findings R2-3 + R3-3: jede bekannte Ursache bekommt ihre eigene Ueberschrift.
          // "Verbindung fehlgeschlagen" wuerde den Nutzer bei R2-3 nach einer
          // Netzwerkstoerung suchen lassen statt nach dem Haekchen, und bei R3-3 einen
          // Google-Ausfall wie einen Fehler seines Kontos aussehen lassen.
          // Kein Aufruf landet je im Erfolgszweig, also laufen weder fetch-account-data
          // noch der Sync-Dialog gegen ein Konto, das gar nicht existiert.
          const copy = resolveOAuthErrorCopy(event.data.code, t);
          toast.error(copy?.title ?? t('oauth.connectionFailed'), {
            description: copy?.description ?? (event.data.error || t('oauth.connectionFailedDesc')),
          });
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);

    } catch (error) {
      setIsConnecting(false);
      console.error('Error initiating Google OAuth:', error);
      Sentry.captureException(error, { tags: { feature: 'auth', action: 'initiate-oauth' } });
      toast.error(t('status.error'), {
        description: t('oauth.initFailed'),
      });
    }
  };

  const handleSyncComplete = () => {
    setShowSyncProgress(false);
    refreshSelection();
    toast.success(t('status.success'), {
      description: t('oauth.connectSuccess'),
    });
    onSuccess?.();
  };

  const syncDialog = showSyncProgress && googleAccountId && (
    <SyncProgressDialog
      open={showSyncProgress}
      googleAccountId={googleAccountId}
      onComplete={handleSyncComplete}
    />
  );

  if (children) {
    return (
      <>
        <div onClick={handleGoogleConnect} className={isConnecting ? "pointer-events-none opacity-50" : "cursor-pointer"}>
          {children}
        </div>
        {syncDialog}
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleGoogleConnect}
        variant="outline"
        className="w-full"
        disabled={isConnecting}
      >
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {t('oauth.connectGoogle')}
      </Button>

      {syncDialog}
    </>
  );
};

export default GoogleOAuthButton;
